import React, { useState } from "react";
import ComponentWrapper from "../../../common/components/ComponentWrapper";
import { useGetRoadmapItems } from "../api/queries";
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext";
import { useCustomer } from "../../../common/contexts/CustomerContext";
import Spinner from "@murphy-frontend/web-core/components/Spinner";
import ColumnGroupTable from "@murphy-frontend/web-core/components/ColumnGroupTable";
import { createColumnGroup, getColSizeFlexBasedOnScreen } from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils";
import MurphyDatePicker from "@murphy-frontend/web-core/components/MurphyDatePicker";
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries";
import { useUpdateRoadmapItem } from "../api/mutations";
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext";
import dayjs from "dayjs";
import CreateableMurphySelect from "@murphy-frontend/web-core/components/MurphySelect/CreateableMurphySelect";
import { ActionMeta, SingleValue } from "react-select";

interface DueDateModalContentProps {
  initialDate: Date | null;
  onSave: (date: Date | null) => void;
  onCancel: () => void;
  isPending: boolean;
  timeZone: string;
}

const DueDateModalContent: React.FC<DueDateModalContentProps> = ({ initialDate, onSave, onCancel, isPending, timeZone }) => {
  const [localDate, setLocalDate] = useState<Date | null>(initialDate);
  return (
    <div
      style={{
        minWidth: 280,
        padding: 24,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      <MurphyDatePicker
        isControlled={false}
        selected={localDate}
        onChange={setLocalDate}
        locale={timeZone}
        showTime={false}
        withPortal={true}
      />
      <div style={{ marginTop: 16 }}>
        <button onClick={() => onSave(localDate)} disabled={isPending || !localDate}>Save</button>
        <button onClick={onCancel} style={{ marginLeft: 8 }} disabled={isPending}>Cancel</button>
      </div>
    </div>
  );
};

interface ResponsibleModalContentProps {
  initialCustomerUserId: string | undefined;
  initialName: string;
  options: { label: string; value: string }[];
  onSave: (customerUserId: string | null, name: string) => void;
  onCancel: () => void;
  isPending: boolean;
}

const ResponsibleModalContent: React.FC<ResponsibleModalContentProps> = ({
  initialCustomerUserId,
  initialName,
  options,
  onSave,
  onCancel,
  isPending,
}) => {
  // Find the initial selection based on customerUserId if it exists, otherwise use the name
  const getInitialValue = () => {
    if (initialCustomerUserId) {
      const userOption = options.find(opt => opt.value === initialCustomerUserId);

      if (userOption) {
        return userOption;
      }
    }

    // If no userId match found, or no userId, use the name as free text
    if (initialName) {
      return {
        label: initialName,
        value: initialName
      };
    }

    return null;
  };

  const [localValue, setLocalValue] = useState<{ label: string; value: string } | null>(getInitialValue());

  const handleSave = () => {
    if (!localValue) {
      return;
    }

    // Check if the selected value is from our user options (has a matching customerUserId)
    const selectedUser = options.find(opt => opt.value === localValue.value);

    if (selectedUser) {
      // It's a user from the dropdown - send customerUserId and the user's name
      onSave(selectedUser.value, selectedUser.label);
    } else {
      // It's free text - send null for customerUserId and the text as name
      onSave(null, localValue.value);
    }
  };

  return (
    <div style={{ minWidth: 320, minHeight: 400, padding: 24, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <CreateableMurphySelect
        isClearable
        isSearchable
        isLoading={isPending}
        options={options}
        value={localValue}
        onChange={(val: SingleValue<{ label: string; value: string }>, _action: ActionMeta<{ label: string; value: string }>) => {
          setLocalValue(val ?? null);
        }}
        placeholder="Select or enter responsible"
        createLabel="Fritext ansvarig: "
      />
      <div style={{ marginTop: 16 }}>
        <button onClick={handleSave} disabled={isPending || !localValue}>Save</button>
        <button onClick={onCancel} style={{ marginLeft: 8 }} disabled={isPending}>Cancel</button>
      </div>
    </div>
  );
};

const TmsRoadmapPage: React.FC = () => {
  const { translations, timeZone } = useUserPreferences();
  const { customer } = useCustomer();
  const customerId = customer?.Id;
  const { data, isLoading, isError } = useGetRoadmapItems(customerId);
  const roadmapItems = data?.RoadmapItems || [];

  const {
    isLoading: isLoadingCustomerUsers,
    data: customerUsers
  } = useGetUsers(customer?.Id)

  const { mutate: updateRoadmapItem, isPending } = useUpdateRoadmapItem(customerId);
  const { openModal, closeModal } = useModal();

  // Sorting state
  const [sortedByColumn, setSortedByColumn] = useState<string>("Description");
  const [isSortedAscending, setIsSortedAscending] = useState<boolean>(true);

  const sortByColumn = (columnKey: string) => {
    if (sortedByColumn === columnKey) {
      setIsSortedAscending(!isSortedAscending);
    } else {
      setSortedByColumn(columnKey);
      setIsSortedAscending(true);
    }
  };

  // State for hover effects - now tracking both due date and responsible columns
  const [hoveredDueDateRowIdx, setHoveredDueDateRowIdx] = useState<number | null>(null);
  const [hoveredResponsibleRowIdx, setHoveredResponsibleRowIdx] = useState<number | null>(null);

  // Open modal for editing due date
  const handleEditDueDate = (rowIdx: number, currentDate: string) => {
    openModal({
      title: "Edit due date",
      body: (
        <DueDateModalContent
          initialDate={currentDate ? new Date(currentDate) : null}
          onSave={(date) => handleDueDateSave(rowIdx, date)}
          onCancel={handleCancelEditDueDate}
          isPending={isPending}
          timeZone={timeZone}
        />
      ),
      hideButton: true,
      type: "info",
      position: "center"
    });
  };

  // Save handler closes modal on success
  const handleDueDateSave = (rowIdx: number, date: Date | null) => {
    const item = roadmapItems[rowIdx];

    if (!item || !item.CustomerRoadmapItemId) {
      return;
    }

    updateRoadmapItem(
      {
        roadmapItemId: item.CustomerRoadmapItemId,
        updateRequest: { DueDate: date ? dayjs(date).format("YYYY-MM-DD") : undefined }
      },
      {
        onSuccess: () => {
          closeModal();
        },
        onError: (_error: unknown) => { }
      }
    );
  };

  // Cancel handler closes modal and clears state
  const handleCancelEditDueDate = () => {
    closeModal();
  };

  const useResponsibleOptions = () => {
    if (isLoadingCustomerUsers || !customerUsers) {
      return [];
    }

    return customerUsers.map(user => ({
      label: user.Username || user.Email,
      value: user.CustomerUserId
    }));
  };

  const responsibleOptions = useResponsibleOptions();

  const handleEditResponsible = (rowIdx: number, currentCustomerUserId: string | undefined, currentName: string) => {
    openModal({
      title: "Edit Responsible",
      body: (
        <ResponsibleModalContent
          initialCustomerUserId={currentCustomerUserId}
          initialName={currentName}
          options={responsibleOptions}
          onSave={(customerUserId, name) => handleResponsibleSave(rowIdx, customerUserId, name)}
          onCancel={handleCancelEditResponsible}
          isPending={isPending}
        />
      ),
      hideButton: true,
      type: "info",
      position: "center"
    });
  };

  const handleResponsibleSave = (rowIdx: number, customerUserId: string | null, name: string) => {
    const item = roadmapItems[rowIdx];

    if (!item || !item.CustomerRoadmapItemId) {
      return;
    }

    updateRoadmapItem(
      {
        roadmapItemId: item.CustomerRoadmapItemId,
        updateRequest: {
          ResponsibleCustomerUserId: customerUserId,
          ResponsibleContact: name
        }
      },
      {
        onSuccess: () => {
          closeModal();
        },
        onError: (_error: unknown) => { }
      }
    );
  };

  const handleCancelEditResponsible = () => {
    closeModal();
  };

  // Define columns
  const columns = [
    {
      columnKey: "BlockAndDescription",
      displayName: `${translations.tmsRoadmapBlock || "Block"}/${translations.tmsRoadmapDescription || "Description"}`,
      isSortable: true,
      size: getColSizeFlexBasedOnScreen("md"),
      onClickColumn: sortByColumn,
    },
    {
      columnKey: "Category",
      displayName: translations.tmsRoadmapCategory || "Category",
      isSortable: false,
      size: getColSizeFlexBasedOnScreen("xs"),
    },
    {
      columnKey: "DueDate",
      displayName: translations.tmsRoadmapDueDate || "Due date",
      isSortable: true,
      size: getColSizeFlexBasedOnScreen("xs"),
      onClickColumn: sortByColumn,
    },
    {
      columnKey: "Status",
      displayName: translations.tmsRoadmapStatus || "Status",
      isSortable: true,
      size: getColSizeFlexBasedOnScreen("xs"),
      onClickColumn: sortByColumn,
    },
    {
      columnKey: "Responsible",
      displayName: translations.tmsRoadmapResponsible || "Responsible",
      isSortable: true,
      size: getColSizeFlexBasedOnScreen("xs"),
      onClickColumn: sortByColumn,
    },
  ];

  // Sort items
  const sortedItems = [...roadmapItems];
  if (sortedByColumn && columns.find(c => c.columnKey === sortedByColumn)?.isSortable) {
    sortedItems.sort((a, b) => {
      let valA = "";
      let valB = "";
      switch (sortedByColumn) {
        case "BlockAndDescription":
          valA = a.Description || "";
          valB = b.Description || "";
          break;
        case "DueDate":
          valA = a.DueDate || "";
          valB = b.DueDate || "";
          break;
        case "Status":
          valA = a.Status || "";
          valB = b.Status || "";
          break;
        case "Responsible":
          valA = a.ResponsibleUserOrContactName || "";
          valB = b.ResponsibleUserOrContactName || "";
          break;
        default:
          valA = "";
          valB = "";
      }
      if (isSortedAscending) {
        return String(valA).localeCompare(String(valB));
      } else {
        return String(valB).localeCompare(String(valA));
      }
    });
  }

  // Build rows
  const rows = sortedItems.map((item, idx) => [
    {
      id: `blockAndDescription-${idx}`,
      rowId: idx,
      columnKey: "BlockAndDescription",
      displayValue: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <span style={{ fontWeight: 600, marginBottom: 4 }}>
            {item.BlockName}
          </span>
          <span>
            {item.Description || "-"}
          </span>
        </div>
      ),
      size: getColSizeFlexBasedOnScreen("md"),
    },
    {
      id: `category-${idx}`,
      rowId: idx,
      columnKey: "Category",
      displayValue: item.CategoryName,
      size: getColSizeFlexBasedOnScreen("xs"),
    },
    {
      id: `duedate-${idx}`,
      rowId: idx,
      columnKey: "DueDate",
      displayValue: (
        <div
          onClick={() => handleEditDueDate(idx, item.DueDate)}
          style={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            background: hoveredDueDateRowIdx === idx ? "#f0f0f0" : "transparent"
          }}
          tabIndex={0}
          role="button"
          aria-label="Edit due date"
          onMouseEnter={() => setHoveredDueDateRowIdx(idx)}
          onMouseLeave={() => setHoveredDueDateRowIdx(null)}
        >
          {item.DueDate ? dayjs(item.DueDate).format("YYYY-MM-DD") : "-"}
        </div>
      ),
      size: getColSizeFlexBasedOnScreen("xs"),
    },
    {
      id: `status-${idx}`,
      rowId: idx,
      columnKey: "Status",
      displayValue: item.Status || "-",
      size: getColSizeFlexBasedOnScreen("xs"),
    },
    {
      id: `responsible-${idx}`,
      rowId: idx,
      columnKey: "Responsible",
      displayValue: (
        <div
          onClick={() => handleEditResponsible(idx, item.ResponsibleCustomerUserId, item.ResponsibleUserOrContactName)}
          style={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            background: hoveredResponsibleRowIdx === idx ? "#f0f0f0" : "transparent"
          }}
          tabIndex={0}
          role="button"
          aria-label="Edit responsible"
          onMouseEnter={() => setHoveredResponsibleRowIdx(idx)}
          onMouseLeave={() => setHoveredResponsibleRowIdx(null)}
        >
          {item.ResponsibleUserOrContactName || "-"}
        </div>
      ),
      size: getColSizeFlexBasedOnScreen("xs"),
    },
  ]);

  const columnGroup = createColumnGroup("", columns, rows);

  return (
    <div>
      <ComponentWrapper active={3} subMenuActive={3}>
        <h1 className="dashboard__header">
          {translations.tmsRoadmapTitle || "Roadmap"}
        </h1>
        {isLoading && <Spinner isGlobal />}
        {isError && (
          <div className="error-message">
            {translations.tmsRoadmapError || "Failed to load roadmap items."}
          </div>
        )}
        {!isLoading && !isError && (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div style={{ width: '80rem' }}>
              <ColumnGroupTable
                columngroups={[columnGroup]}
                sortedByColumn={sortedByColumn}
                isSortedAscending={isSortedAscending}
                hidePageInputs={true}
                isFluidTable={true}
              />
            </div>
          </div>
        )}
      </ComponentWrapper>
    </div>
  );
};

export default TmsRoadmapPage;